import { Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { fragQuery, User } from '@models';
import { snapshotManager } from '@app/utilities/snapshot/snapshot-manager';
import { DateFormatConfig } from '@app/utilities/date-format-config';

@Component({
    selector: 'snapshot-card-analysis',
    templateUrl: './snapshot-card-analysis.component.html',
    styleUrls: ['./snapshot-card-analysis.component.scss']
})
export class SnapshotCardAnalysisComponent implements OnInit {
    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly user: User,
        private readonly dateFormat: DateFormatConfig
    ) { }

    @Input() snapType: string = 'analysis';
    @Input() imagesrc: string = '';
    @Input() downloadButton: boolean = false;
    @Input() deviceId: number | null = null;

    public readonly device$ = this.fragQuery.device$;
    public readonly percents$ = this.fragQuery.newPercents$;
    public readonly unitSystem: string = this.user.particleUnit;
    public readonly currentInterval$ = new BehaviorSubject(null);
    public readonly dateTimeFormat = this.dateFormat.getFormat();
    public intervals$ = new BehaviorSubject<fragQuery.timeline.sieveSizesAtPercents.Interval[]>([]);
    public currentIntervalIndex$ = new BehaviorSubject<number>(0)
    public loading: boolean = false;
    private destroy$ = new Subject<void>();
    private readonly nbValuesDxxPercents$ = this.fragQuery.nbValuesDxxPercents$;
    private nbKeys: Map<number, string> = new Map<number, string>();

    public ngOnInit() {
        this._updateFragQueryData();
        this._initializeSnapshotManagerBindings()

        this.nbValuesDxxPercents$.pipe(takeUntil(this.destroy$)).subscribe(nbKeys => {
            this.nbKeys = nbKeys;
        });
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public handleNextClick() {
        snapshotManager.next()
    }

    public handlePreviousClick() {
       snapshotManager.previous()
    }

    public mapDxxValues(dvalues: number[]): Map<number, number> {
        const newMappedValues = new Map<number, number>(
            Array.from(this.nbKeys.keys()).map((key, index) => [key, dvalues[index + 1] ?? 0])
        );
        return newMappedValues;
    }

    private _initializeSnapshotManagerBindings() {
        snapshotManager.intervals$.pipe(takeUntil(this.destroy$)).subscribe(intervals => {
            this.intervals$.next(intervals);
        });

        snapshotManager.currentIndex$.pipe(takeUntil(this.destroy$)).subscribe(currentIndex => {
            this.currentIntervalIndex$.next(currentIndex);
        });

        snapshotManager.currentInterval$.pipe(takeUntil(this.destroy$)).subscribe(interval => {
            this.currentInterval$.next(interval)
        });

        snapshotManager.loading$.pipe(takeUntil(this.destroy$)).subscribe(val => this.loading = val);
    }

    private _updateFragQueryData(): void {
        if (this.deviceId !== null) {
            this.fragQuery.device$.pipe(takeUntil(this.destroy$)).subscribe(currentDevice => {
                if (!currentDevice || currentDevice.id !== this.deviceId) {
                    this.fragQuery.devices$
                        .pipe(take(1), takeUntil(this.destroy$))
                        .subscribe(devices => {
                            const matchingDevice = devices.find(device => device.id === this.deviceId);
                            if (matchingDevice) {
                                this.fragQuery.deviceUuid$.next(matchingDevice.uuid)
                            } else {
                                console.error('Device not found for deviceId:', this.deviceId);
                            }
                        });
                }
            });
        }
    }
}